//#region Imports

import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { InstanceProxy } from '../../models/proxies/instance.proxy';
import { HttpAsyncService } from '../../modules/http-async/services/http-async.service';

//#endregion

/**
 * A classe que representa o serviço que lida com as instâncias
 */
@Injectable({
  providedIn: 'root',
})
export class InstanceService {

  //#region Constructor

  /**
   * Construtor padrão
   */
  constructor(
    protected readonly router: Router,
    protected readonly http: HttpAsyncService,
    protected readonly title: Title,
  ) { }

  //#endregion

  //#region Private Properties

  /**
   * As informações da instância atual
   */
  public currentInstance?: InstanceProxy;

  //#endregion

  //#region Public Methods

  /**
   * Método que realiza a inicialização do serviço de instância
   */
  public async initialize(): Promise<void> {
    const subdomain = this.getCurrentSubdomain();

    await this.loadInstanceBySubdomain(subdomain);

    (window as any).__uniGetInstance = () => this.currentInstance;
    (window as any).__uniSetCurrentInstanceBySubdomain = (domain) => this.loadInstanceBySubdomain(domain);
    (window as any).__uniClearForcedSubdomain = () => localStorage.removeItem(environment.keys.forceSubdomain);
    (window as any).__uniForceSubdomain = (domain) => {
      localStorage.setItem(environment.keys.forceSubdomain, domain);

      this.loadInstanceBySubdomain(domain);
    };
  }

  /**
   * Método que retorna o subdomínio atual
   */
  public getCurrentSubdomain(): string {
    const overrideSubdomain = localStorage.getItem(environment.keys.forceSubdomain);

    if (overrideSubdomain)
      return overrideSubdomain;

    const url = location.hostname;

    if (url === 'localhost')
      return environment.api.baseUrl.split('.')[0].split('/').slice(-1)[0];

    if (
      location.host === 'backoffice.uniflorestas.com'
    ) return 'uniflorestas';
    
    if (
      location.host === 'backoffice.cursoregenerar.com.br'
      || location.host === 'backoffice.cursoregenerar.com'
    ) return 'regenerar';
     
    if (
      environment.production === false &&
      (location.host === 'backoffice-dev.cursoregenerar.com.br'
      || location.host === 'backoffice-dev.cursoregenerar.com')
    ) return 'regenerar';

    return url.split('.')[0] || 'localhost';
  }

  //#endregion

  //#region Private Methods

  /**
   * Método que carrega as informações de uma instância pelo subdomínio
   *
   * @param subdomain O subdomínio
   */
  private async loadInstanceBySubdomain(subdomain: string): Promise<void> {
    const { error, success } = await this.http.get<InstanceProxy>(`/instances/${subdomain}`);

    if ((error && error.status === 404) || !success)
      return void await this.router.navigateByUrl('/instance-not-found');

    this.currentInstance = success;

    this.setupInstance(success);
  }

  /**
   * Método que realiza as configurações iniciais da instância
   *
   * @param instance As informações da instância
   */
  private setupInstance(instance: InstanceProxy): void {
    this.title.setTitle(`Painel - ${instance.fantasyName}`);

    const faviconIcon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');

    if (!faviconIcon)
      return;

    faviconIcon.href = instance.faviconUrl;
  }

  //#endregion

}
